import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeftIcon, Calendar as CalendarIcon } from 'lucide-react';
import Navbar from '@/components/modules/Navbar';
import {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  SortableTableHead
} from '@/components/ui/table';
import { Calendar } from '@/components/ui/calendar';
import useAxios from '@/hooks/use-axios';
import Image from '@/components/modules/Image';
import UserAgentChart from "@/components/modules/UserAgentChart";
import OverTimeChart from "@/components/modules/OverTimeChart";

export default function DetailTracking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const api = useAxios();

  const [detailTracking, setDetailTracking] = useState();
  const [dataCountries, setDataCountries] = useState([]);
  const [dataCities, setDataCities] = useState([]);
  
  const fetchDetailTracking = async () => {
    try {
      const response = await api.get(`qr-scan/stats/${id}`);
      setDetailTracking(response.data);
      setDataCountries(response.data.country_stats.map((item, index) => ({...item, no: index + 1})))
      setDataCities(response.data.city_stats.map((item, index) => ({...item, no: index + 1})))
      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchDetailTracking();
  }, []);

    const downloadQRImage = (qrPreview) => {
    const url = qrPreview;
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `preview_qr.png`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return;
  };

  const [sortConfig, setSortConfig] = useState({ key: "no", direction: "desc" })
  const sortedDataCountries = useMemo(() => {
    if (!sortConfig.key) return dataCountries
    return [...dataCountries].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === "asc" ? -1 : 1
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === "desc" ? 1 : -1
      return 0
    })
  }, [dataCountries, sortConfig])

  const [sortConfigCity, setSortConfigCity] = useState({ key: "no", direction: "desc" })
  const sortedDataCities = useMemo(() => {
    if (!sortConfigCity.key) return dataCities
    return [...dataCities].sort((a, b) => {
      if (a[sortConfigCity.key] < b[sortConfigCity.key]) return sortConfigCity.direction === "asc" ? -1 : 1
      if (a[sortConfigCity.key] > b[sortConfigCity.key]) return sortConfigCity.direction === "desc" ? 1 : -1
      return 0
    })
  }, [dataCities, sortConfigCity])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col gap-8 mb-10">
      <Navbar hideNavigation />
      <section className="container px-4 flex flex-col gap-8 mx-auto">
        <div className="flex flex-col w-full gap-4">
          <div className="flex flex-col gap-8 ">
            <Button variant="dark" className="w-fit" onClick={() => navigate(-1)}>
              {' '}
              <ArrowLeftIcon className="mr-2" /> Kembali
            </Button>
            <div className="flex flex-col lg:flex-row justify-between h-36 lg:h-fit">
              <h1 className="leading-[2.375rem] text-3xl font-semibold">Statistik Tracking</h1>
              <div className="flex gap-4 w-full lg:w-fit">
                {/* <Button variant="flat">
                  <i className="garuda-palette" />
                  Edit
                </Button>
                <Button variant="flat">
                  <i className="garuda-delete" />
                  Delete
                </Button> */}
                <Button className="lg:min-w-[166px]" onClick={() => downloadQRImage(detailTracking?.content)}>Download QR</Button>
              </div>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row gap-8">
            <div className=" flex flex-col w-full gap-8">
              <div className="card !p-6 flex-1 w-full grid grid-cols-2 gap-2 max-h-[112px]">
                <h5 className="text-bold text-muted leading-7">Created Date :</h5>
                <h5 className="text-bold text-muted leading-7">Link</h5>
                <h5 className="text-bold leading-7">{ detailTracking?.created_date }</h5>
                <h5 className="text-bold leading-7">{ detailTracking?.link }</h5>
              </div>
              <div className=" flex flex-col lg:flex-row w-full gap-8">
                <div className="card !p-6 flex flex-col flex-1 gap-2">
                  <h5 className="text-bold text-muted leading-7">Total Scans</h5>
                  <h5 className="text-bold leading-7 text-2xl">{ detailTracking?.total_scans }</h5>
                </div>

                <div className="card !p-6 flex flex-col flex-1 gap-2">
                  <h5 className="text-bold text-muted leading-7">Unique Scans</h5>
                  <h5 className="text-bold leading-7 text-2xl">{ detailTracking?.unique_scans }</h5>
                </div>
              </div>
            </div>
            <div className="card !p-[28px] w-[264px] h-[256px]">
              <Image content={detailTracking?.content} popupImage={!!detailTracking?.content} className="w-[200px] h-[200px] mx-auto rounded-lg" />
            </div>
          </div>
        </div>
      </section>

      <section className="container px-4 flex flex-col gap-4 mx-auto">
        <div className="flex justify-between">
          <h1 className="leading-[2.375rem] text-3xl font-semibold">Scans</h1>
          <div className="flex gap-4">
            <CalendarIcon />
            <Input type="date" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card !p-6 flex flex-col"><OverTimeChart chartData={detailTracking?.over_time} /></div>
          <div className="card !p-6 flex flex-col max-h-[548px]">
              <UserAgentChart chartData={detailTracking?.operating_systems} />
          </div>
          <div className="card !p-6 flex flex-col gap-4 max-h-[548px]">
            <p className="text-[1.125rem] leading-7 font-semibold">TOP COUNTRIES</p>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    label="No"
                    sortKey="no"
                    sortConfig={sortConfig}
                    onSort={(key, dir) => setSortConfig({ key, direction: dir })}
                    className="w-[60px] !py-4"
                  />
                  <SortableTableHead
                    label="Negara"
                    sortKey="country"
                    sortConfig={sortConfig}
                    onSort={(key, dir) => setSortConfig({ key, direction: dir })}
                    className="!py-4"
                  />
                  <SortableTableHead
                    label="Scans"
                    sortKey="count"
                    sortConfig={sortConfig}
                    onSort={(key, dir) => setSortConfig({ key, direction: dir })}
                    className="text-center w-[120px] !py-4"
                    alignClassName="justify-center"
                  />
                  <SortableTableHead
                    label="%"
                    sortKey="percentage"
                    sortConfig={sortConfig}
                    onSort={(key, dir) => setSortConfig({ key, direction: dir })}
                    className="text-center w-[80px] !py-4"
                    alignClassName="justify-center"
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                { sortedDataCountries.length <= 0 ?
                  <TableRow>
                    <TableCell className="font-medium text-center text-muted !py-4" colSpan={4}>Belum ada data</TableCell>
                  </TableRow>
                  :
                  sortedDataCountries.map((country, index) => (
                    <TableRow key={index}>
                      <TableCell className="!py-4 ">{country.no}</TableCell>
                      <TableCell className="!py-4 ">{country.country}</TableCell>
                      <TableCell className="!py-4 text-center">{country.count}</TableCell>
                      <TableCell className="!py-4 text-center">{country.percentage}</TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </div>
          <div className="card !p-6 flex flex-col gap-4">
            <p className="text-[1.125rem] leading-7 font-semibold">TOP CITIES</p>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    label="No"
                    sortKey="no"
                    sortConfig={sortConfigCity}
                    onSort={(key, dir) => setSortConfigCity({ key, direction: dir })}
                    className="w-[60px] !py-4"
                  />
                  <SortableTableHead
                    label="Kota"
                    sortKey="city"
                    sortConfig={sortConfigCity}
                    onSort={(key, dir) => setSortConfigCity({ key, direction: dir })}
                    className="!py-4"
                  />
                  <SortableTableHead
                    label="Scans"
                    sortKey="count"
                    sortConfig={sortConfigCity}
                    onSort={(key, dir) => setSortConfigCity({ key, direction: dir })}
                    className="text-center w-[120px] !py-4"
                    alignClassName="justify-center"
                  />
                  <SortableTableHead
                    label="%"
                    sortKey="percentage"
                    sortConfig={sortConfigCity}
                    onSort={(key, dir) => setSortConfigCity({ key, direction: dir })}
                    className="text-center w-[80px] !py-4"
                    alignClassName="justify-center"
                  />
                </TableRow>
              </TableHeader>
                <TableBody>
                { sortedDataCities.length <= 0 ?
                  <TableRow>
                    <TableCell className="font-medium" colSpan={4}>Belum ada data</TableCell>
                  </TableRow>
                  :
                  sortedDataCities.map((city, index) => (
                  <TableRow key={index}>
                      <TableCell className="!py-4">{city.no}</TableCell>
                      <TableCell className="!py-4 ">{city.city}</TableCell>
                      <TableCell className="!py-4 text-center">{city.count}</TableCell>
                      <TableCell className="!py-4 text-center">{city.percentage}</TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </div>
        </div>
      </section>
    </div>
  );
}
